import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { formatMoney } from "../lib/api";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, update, remove, subtotal, clear } = useCart();
  const nav = useNavigate();

  if (items.length === 0) {
    return (
      <main className="max-w-[900px] mx-auto px-4 py-20 text-center" data-testid="empty-cart">
        <ShoppingBag size={64} className="mx-auto mb-6 text-zinc-400" />
        <h1 className="font-display text-4xl font-extrabold mb-3">Your cart is empty</h1>
        <p className="text-zinc-600 mb-6">Search by VIN or browse our catalog to find the part you need.</p>
        <Link to="/search" className="btn-primary">Start shopping</Link>
      </main>
    );
  }

  return (
    <main className="max-w-[1200px] mx-auto px-4 lg:px-8 py-12" data-testid="cart-page">
      <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight mb-8">Your cart</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.part_id} className="bg-white border border-[#E4E4E7] p-4 flex gap-4" data-testid={`cart-item-${item.part_id}`}>
              <img src={item.image_url} alt={item.name} className="w-24 h-24 object-cover bg-zinc-100" />
              <div className="flex-1 min-w-0">
                <Link to={`/parts/${item.part_id}`} className="font-semibold hover:text-[#009A44] line-clamp-2">{item.name}</Link>
                <div className="mt-2 text-sm text-zinc-500">{formatMoney(item.price, item.currency)} each</div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex items-center border border-[#121212]">
                    <button onClick={() => update(item.part_id, item.quantity - 1)} className="p-1.5 hover:bg-zinc-100"><Minus size={12} /></button>
                    <div className="px-4 py-1 text-sm font-semibold">{item.quantity}</div>
                    <button onClick={() => update(item.part_id, item.quantity + 1)} className="p-1.5 hover:bg-zinc-100"><Plus size={12} /></button>
                  </div>
                  <button onClick={() => remove(item.part_id)} className="text-[#EF3340] hover:underline text-xs font-semibold uppercase flex items-center gap-1" data-testid={`remove-${item.part_id}`}>
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
              <div className="font-display text-xl font-extrabold whitespace-nowrap">
                {formatMoney(item.price * item.quantity, item.currency)}
              </div>
            </div>
          ))}
          <button onClick={clear} className="text-xs text-zinc-500 hover:text-[#EF3340] underline">Clear cart</button>
        </div>

        <aside className="bg-white border-2 border-[#121212] p-6 h-fit sticky top-24" data-testid="cart-summary">
          <div className="font-display font-extrabold text-xl mb-5 uppercase tracking-tight">Summary</div>
          <div className="space-y-3 text-sm mb-5">
            <Row label="Subtotal" value={formatMoney(subtotal)} />
            <Row label="Shipping" value="Calculated at checkout" small />
            <Row label="Platform fee (5%)" value={formatMoney(subtotal * 0.05)} />
          </div>
          <div className="border-t border-[#E4E4E7] pt-4 flex justify-between items-baseline mb-6">
            <div className="font-semibold uppercase text-xs tracking-widest">Est. total</div>
            <div className="font-display text-3xl font-extrabold">{formatMoney(subtotal * 1.05)}</div>
          </div>
          <button onClick={() => nav("/checkout")} className="btn-primary w-full" data-testid="checkout-button">
            Proceed to checkout <ArrowRight size={16} />
          </button>
          <div className="mt-4 text-xs text-zinc-500 leading-relaxed">
            Payment is held for up to <b>48 hours</b> while we confirm availability with our sourcing network. You're only fully charged once the part is locked in.
          </div>
        </aside>
      </div>
    </main>
  );
}

function Row({ label, value, small }) {
  return (
    <div className="flex justify-between">
      <div className={small ? "text-zinc-500" : "text-zinc-700"}>{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
